import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { Container, ScreenWrapper } from './SignUpFlowPage.styles';
import { useAppDispatch, useTypedSelector } from '../../store/store';
import { SignUpScreen } from './SignUpScreen/SignUpScreen';
import { authWithPartnerToken } from '../../store/slices/me';
import { useQuery } from '../../services/hooks/useQuery';
import { useClientType } from '../../services/hooks/useClientType';
import { useTrackEvent } from '../../common/hooks/useTrackEvent';
import { EventNames } from '../../common/constants/constants';
import { TICKET } from '../CAV6/HomeScreen/HomeScreen.styles';
import { useHumanSignal } from '../../common/hooks/useHumanSignal';
import { getCachedUploadSpeed } from '../VideoToolPage/utils';
import { PENDING_UPLOAD_KEY } from '../../services/upload/pendingUploadStore';
import { captureDeviceLocationIfGranted } from '../../services/analytics/deviceLocation';
import {
  getReferrerType,
  installLeaveWatch,
  markArrival,
  msSinceArrival,
  msSinceAttempt,
  msSinceLastProgress,
  markRecorderRetry,
} from '../../services/analytics/telemetry';
import { clearRecorderReopen, shouldReopenRecorder } from '../VideoToolPage/RecordVideoComponent/recorderReload';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface Props {
  isDataLoading: boolean;
}

export const SignUpFlowPage: FC<Props> = ({ isDataLoading }) => {
  const dispatch = useAppDispatch();
  const [isRecordVideoScreen, setIsRecordVideoScreen] = useState(false);
  // The recorder reloads the page once when the browser keeps refusing the
  // camera after the person allowed it (see recorderReload.ts); come back
  // straight into the recorder, as a retry. Waits for the data: HomePage
  // unmounts and remounts this page while it refetches the account, which
  // closed a recorder opened at first paint.
  useEffect(() => {
    if (isDataLoading || !shouldReopenRecorder()) return;
    clearRecorderReopen();
    markRecorderRetry();
    setIsRecordVideoScreen(true);
  }, [isDataLoading]);
  const { isTrubluClient } = useClientType();
  // Non-TruBlu clients accept the terms implicitly via the "By uploading, you
  // agree…" line on the ticket; TruBlu keeps its explicit checkbox.
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean | null>(!isTrubluClient || null);

  const query = useQuery();
  const partnerToken = query.get('partner-token');

  const {
    account: { id: accountId },
  } = useTypedSelector((state) => state.account);
  const { trackEvent } = useTrackEvent();

  useHumanSignal();

  // Ticket first paint = arrival. Also arms the single pagehide watch that
  // screens feed a stage into (upload_abandoned / complete_screen_leave).
  // A layout effect, so arrival is recorded before any child's ordinary
  // effects (React runs child effects first; ticket_ready and the legacy page
  // view used to go out ahead of app_arrival).
  useIsomorphicLayoutEffect(() => {
    if (!markArrival()) return;
    const params = new URLSearchParams(window.location.search);
    let hadPending = false;
    try {
      hadPending = !!localStorage.getItem(PENDING_UPLOAD_KEY);
    } catch {
      hadPending = false;
    }
    trackEvent({
      action: EventNames.app_arrival,
      referrer_type: getReferrerType(),
      has_qrcode_param: params.get('qrcode') === 'true',
      has_cid: !!params.get('cid'),
      has_association_id: !!params.get('associationId'),
      had_pending_upload: hadPending,
      t_since_nav_ms: Math.round(performance.now()),
      speed_cached: getCachedUploadSpeed() != null,
    });
    // Only reads the location when the site already has permission; never asks.
    // Once read, it rides on every later event as the device_geo user property.
    captureDeviceLocationIfGranted((result) => trackEvent({ action: EventNames.device_location_result, ...result }));
    installLeaveWatch((stage, extra) => {
      if (stage === 'complete') {
        trackEvent({ action: EventNames.complete_screen_leave, ...extra });
        return;
      }
      trackEvent({
        action: EventNames.upload_abandoned,
        stage,
        elapsed_ms: msSinceAttempt(),
        last_progress_age_ms: msSinceLastProgress(),
        t_since_arrival_ms: msSinceArrival(),
        ...extra,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (partnerToken) {
      dispatch(authWithPartnerToken({ accountId, token: partnerToken }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // complete_screen_page_view is fired by UploadFlowCarousel when the screen
  // is actually shown. The former effect here keyed off "upload in progress
  // and questions answered", which for a second video in the same tab is
  // true at attempt start (question state is not reset between attempts),
  // so it fired a minute early.

  // No intro / launch screen: the video ticket is the first paint. While the
  // campaign is still loading the ticket renders with its buttons disabled.
  return (
    <Container>
      <ScreenWrapper $background={TICKET.pageBg}>
        <SignUpScreen
          isRecordVideoScreen={isRecordVideoScreen}
          setIsRecordVideoScreen={setIsRecordVideoScreen}
          isTermsAccepted={isTermsAccepted}
          onTermsChange={setIsTermsAccepted}
          isDataLoading={isDataLoading}
        />
      </ScreenWrapper>
    </Container>
  );
};
